import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { GST_STATE_NAMES } from '../utils/countriesStates';

const STATES = [...new Set(Object.values(GST_STATE_NAMES))].filter((n) => !['Other Territory', 'Centre Jurisdiction', 'Daman and Diu'].includes(n)).sort();
const messageOf = (e, fallback) => e.response?.data?.message || (typeof e.response?.data === 'string' ? e.response.data : null) || fallback;
const REST = () => ({ id: null, name: 'Everywhere else', states: [], rest: true, baseKg: '1', baseCharge: '', perKgCharge: '' });

/**
 * Org Admin > Online Store > Shipping by weight and place. Off (one flat charge, set above) until chosen. A zone is some states with a charge for the first kg and a charge for
 * each further kg; "Everywhere else" takes every other state. The cart's weight comes from the weights set on Online Store > Products online.
 */
export default function ShippingSettings({ storeSaved }) {
  const [mode, setMode] = useState('FLAT');
  const [zones, setZones] = useState([]);
  const [withoutWeight, setWithoutWeight] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const apply = (d) => {
    setMode(d.mode || 'FLAT');
    setZones((d.zones || []).map((z) => ({ ...z, baseKg: String(z.baseKg ?? ''), baseCharge: String(z.baseCharge ?? ''), perKgCharge: String(z.perKgCharge ?? '') })));
    setWithoutWeight(d.withoutWeight || 0);
  };
  const load = useCallback(async () => {
    try { apply((await axios.get('/api/store/shipping')).data); setLoaded(true); } catch (e) { setLoaded(false); }
  }, []);
  useEffect(() => { load(); }, [load, storeSaved]);

  const taken = useMemo(() => new Set(zones.flatMap((z) => z.states || [])), [zones]);
  const change = (i, patch) => setZones((zs) => zs.map((z, k) => (k === i ? { ...z, ...patch } : z)));
  const addZone = () => setZones((zs) => {
    const rest = zs.filter((z) => z.rest);
    const others = zs.filter((z) => !z.rest);
    return [...others, { id: null, name: '', states: [], rest: false, baseKg: '1', baseCharge: '', perKgCharge: '' }, ...rest];
  });
  const chooseWeight = (m) => {
    setMode(m);
    if (m === 'WEIGHT' && !zones.some((z) => z.rest)) setZones((zs) => [...zs, REST()]);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { mode, zones: zones.map((z) => ({ ...z, baseKg: Number(z.baseKg), baseCharge: Number(z.baseCharge || 0), perKgCharge: Number(z.perKgCharge || 0) })) };
      apply((await axios.put('/api/store/shipping', body)).data);
      toast.success('Saved.');
    } catch (err) {
      toast.error(messageOf(err, 'Could not save the shipping charges.'));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded || !storeSaved) return null;
  return (
    <Card className="mt-3" data-testid="shipping-settings">
      <Card.Body>
        <Card.Title as="h6">How shipping is charged</Card.Title>
        <Form onSubmit={save}>
          <Form.Check type="radio" id="ship-flat" name="ship-mode" label="One flat charge (the Shipping charge above)" checked={mode === 'FLAT'} onChange={() => chooseWeight('FLAT')} />
          <Form.Check type="radio" id="ship-weight" name="ship-mode" label="By weight and place" checked={mode === 'WEIGHT'} onChange={() => chooseWeight('WEIGHT')} data-testid="ship-by-weight" />
          {mode === 'WEIGHT' && (
            <div className="mt-3">
              <p className="small text-muted mb-2">
                The charge is worked out from the cart's weight and the buyer's state: the first charge covers the first kilos, then each further kilo started costs the extra amount (leave it 0 to charge
                the same for any weight). "Free shipping over" above still waives it. Set each product's weight in Online Store &rarr; Products online.
              </p>
              {withoutWeight > 0 && <Alert variant="warning" className="py-2 small" data-testid="no-weight-warning">{withoutWeight} published {withoutWeight === 1 ? 'product has' : 'products have'} no weight yet and will count as weighing nothing.</Alert>}
              {zones.map((z, i) => (
                <Card key={z.id || `new-${i}`} className="mb-2" body data-testid="shipping-zone">
                  <Row className="g-2 align-items-end">
                    <Col md={4}><Form.Label className="small mb-0">Zone</Form.Label><Form.Control size="sm" value={z.name} maxLength={80} placeholder="For example: My state" onChange={(e) => change(i, { name: e.target.value })} disabled={z.rest} /></Col>
                    <Col xs={4} md={2}><Form.Label className="small mb-0">First (kg)</Form.Label><Form.Control size="sm" type="number" min="0.001" step="0.001" value={z.baseKg} onChange={(e) => change(i, { baseKg: e.target.value })} /></Col>
                    <Col xs={4} md={2}><Form.Label className="small mb-0">Charge</Form.Label><Form.Control size="sm" type="number" min="0" step="0.01" value={z.baseCharge} onChange={(e) => change(i, { baseCharge: e.target.value })} /></Col>
                    <Col xs={4} md={2}><Form.Label className="small mb-0">Each extra kg</Form.Label><Form.Control size="sm" type="number" min="0" step="0.01" value={z.perKgCharge} onChange={(e) => change(i, { perKgCharge: e.target.value })} /></Col>
                    <Col md={2} className="text-end">{!z.rest && <Button size="sm" variant="link" className="text-danger" onClick={() => setZones((zs) => zs.filter((_, k) => k !== i))}>Remove</Button>}</Col>
                  </Row>
                  {z.rest
                    ? <div className="small text-muted mt-1">Every state that is not in a zone above.</div>
                    : (
                      <Form.Group className="mt-2">
                        <Form.Label className="small mb-0">States</Form.Label>
                        <Form.Select size="sm" multiple htmlSize={6} value={z.states || []} data-testid="zone-states"
                          onChange={(e) => change(i, { states: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
                          {STATES.filter((s) => !taken.has(s) || (z.states || []).includes(s)).map((s) => <option key={s} value={s}>{s}</option>)}
                        </Form.Select>
                        <Form.Text className="text-muted">Hold Ctrl (Cmd on a Mac) to choose several.</Form.Text>
                      </Form.Group>
                    )}
                </Card>
              ))}
              <Button size="sm" variant="outline-secondary" onClick={addZone} data-testid="add-zone">+ Add a zone</Button>
            </div>
          )}
          <div className="mt-3"><Button type="submit" size="sm" disabled={saving}>Save shipping</Button></div>
        </Form>
      </Card.Body>
    </Card>
  );
}
